/* The parts every Momentum panel is built from.
 *
 * Split out of MomentumHub.jsx at 3,700 lines. These are the pieces CLAUDE.md's
 * screen pattern names: a title that reads as a heading, a Saved read-out where
 * a Save button used to be, the chat pane with the slot its lock card sits in,
 * and the small labels and badges the panels share.
 *
 * NOTHING HERE KNOWS WHAT A PAGE, A MODULE OR AN OFFER IS. Anything that does
 * belongs in that panel's own file — that boundary is the whole point of the
 * split, and it's what stops this becoming the next 3,700-line file.
 */
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Info, Lock, MessageSquare, Plus, Wand2 } from "lucide-react";
import { useBrowseNarrow } from "../BrowseTable";
import { CHAT_HEIGHT } from "../../constants/layout";
import { B, STATUS_DOT, STATUS_LABELS, btnPrimary, input, pDesc, pTitle, pillBase, quickBtn, soonBadge, soonCard } from "./tokens";

/* ── A panel that FLOATS off its button, rather than dropping out of it ──────
   Her report, 2026-09-22, with a screenshot: the offer's "Where it sends
   people" picker opens downward and the "Point this at" panel runs off the
   bottom of the screen, so the sites she is choosing between are below the
   fold with nothing to scroll.

   THE SAME BUG, THE SAME FIX, THE SECOND TIME. The Library picker did this on
   2026-09-17 — also her report, also a panel positioned INSIDE the flow of the
   thing that opened it. `position: absolute; top: calc(100% + 8px)` can only
   ever go down, and it is measured against a parent that has no idea where the
   window ends. So this is that fix, lifted out of libraryPicker.jsx before a
   third copy got written by hand.

   Two rules, and the second is the one the Library version was missing:
   · OPEN WHERE THERE IS ROOM. Below if it fits, otherwise above.
   · NEVER TALLER THAN THE SIDE IT OPENS ON. Picking the roomier side is not
     enough on a phone, where neither side may be tall enough — so the panel
     is capped to what it has and scrolls inside itself. A panel that opens
     upward off the TOP of the screen is the same bug wearing a hat.

   Measured when she opens it, not on render: these buttons sit inside panes
   that scroll and cards that grow, so a position taken any earlier is a
   position from somewhere else. */
export function useAnchoredPanel() {
  const ref = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const rect = () => {
    const r = ref.current?.getBoundingClientRect();
    return r ? { left: r.left, right: r.right, top: r.top, bottom: r.bottom } : { left: 12, right: 12, top: 0, bottom: 0 };
  };
  return {
    ref,
    anchor,
    open: !!anchor,
    openPanel: () => setAnchor(rect()),
    toggle: () => setAnchor((a) => (a ? null : rect())),
    close: () => setAnchor(null),
  };
}

/* The style for that panel. `width` is what it wants; `wants` is how tall it
   would be with nothing in its way. Both are wishes — the viewport wins. */
export function floatingPanel(anchor, { width = 440, wants = 420, gap = 6, edge = 12 } = {}) {
  const vw = typeof window === "undefined" ? 1200 : window.innerWidth;
  const vh = typeof window === "undefined" ? 800 : window.innerHeight;
  const w = Math.min(width, vw - edge * 2);
  const below = vh - (anchor?.bottom || 0) - gap - edge;
  const above = (anchor?.top || 0) - gap - edge;
  /* Down unless it does not fit and up is roomier — so it keeps its usual
     direction wherever it can, and only flips when that would cost her the
     list. */
  const up = below < Math.min(wants, above) && above > below;
  return {
    position: "fixed",
    zIndex: 120,
    left: Math.max(edge, Math.min(anchor?.left || edge, vw - w - edge)),
    width: w,
    ...(up ? { bottom: Math.max(edge, vh - (anchor?.top || 0) + gap) } : { top: (anchor?.bottom || 0) + gap }),
    maxHeight: Math.max(160, up ? above : below),
    overflowY: "auto",
  };
}


/* Two panes need their own breakpoint, not `isMobile`. That flips at 768px of
   WINDOW, but this layout lives inside a 282px app sidebar and the panel's own
   padding — so at 800px there is nowhere near room for a page list beside an
   editor, and it wouldn't collapse until 768. Measured against the space this
   layout actually gets. */
export function useTwoPane(min = 1080) {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.innerWidth >= min);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= min);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [min]);
  return wide;
}


/* The Table / <copy> switch, shared by Website and Course. Table is for scanning
   what's written; the copy view is for writing, with the pages (or modules) down
   the side so she can move between them without going back out to the list each
   time. Only the second tab's word differs, which is what `copyLabel` is. */
export function ViewToggle({ view, setView, copyLabel = "Page copy" }) {
  const tab = (on) => ({
    padding: "7px 15px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${on ? B.teal : B.border2}`,
    background: on ? B.tealQuiet : B.surface,
    color: on ? B.teal : B.mid,
    fontWeight: on ? 700 : 600,
  });
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <button style={tab(view === "table")} onClick={() => setView("table")}>Table</button>
      <button style={tab(view === "copy")} onClick={() => setView("copy")}>{copyLabel}</button>
    </div>
  );
}


/* Teal, not the handoff's gold. Amber is load-bearing in here — a draft dot, a
   stale offer, the next step in a checklist — and an Add button is none of
   those. One component, so Add page / Add module / Add offer can't drift. */
export function AddButton({ label, onClick }) {
  return (
    <button onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 10, background: B.tealQuiet, border: `1px solid ${B.tealLine}`, color: B.teal, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
      <Plus size={15} /> {label}
    </button>
  );
}


// ── Small shared bits ─────────────────────────────────────────────────────────

/* THE ADD MENUS, and they float now too (2026-09-22). Sixteen of them, and the
   ones that matter are the "what does this module need?" / "what kind of
   section?" menus that sit at the BOTTOM of a record she has been writing —
   exactly where the offer's picker was when she reported it.

   IT ANCHORS TO ITSELF. Every call site renders this straight after its Add
   button inside a `position: relative` wrapper, so the button is this element's
   previous sibling and there is nothing for sixteen callers to pass. Measured
   in a layout effect, which runs BEFORE paint, so the one frame it spends
   without a position is never drawn — and hidden anyway, because a menu that
   flashes in the top corner first is worse than one that is late.

   RE-MEASURED ON SCROLL, which the other two pickers do not need: those are
   opened and used in one gesture, while an Add menu can sit open while she
   scrolls the list behind it. Fixed to the window without this, it would slide
   away from its own button. */
export function Menu2({ head, items }) {
  const el = useRef(null);
  const [anchor, setAnchor] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const host = el.current?.previousElementSibling || el.current?.parentElement;
      if (!host) return;
      const r = host.getBoundingClientRect();
      setAnchor({ left: r.left, right: r.right, top: r.top, bottom: r.bottom });
    };
    measure();
    /* Capture, so a scroll inside any pane counts and not just the window's. */
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, []);
  return (
    <div ref={el} style={{
      /* A head and a row each, roughly, so a three-item menu still opens
         downward where a nine-item one would have to flip. */
      ...floatingPanel(anchor, { width: 440, wants: 54 + items.length * 62 }),
      visibility: anchor ? "visible" : "hidden",
      background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 12, padding: 7, boxShadow: "var(--shadow-menu)",
    }}>
      <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: B.label, fontWeight: 700, padding: "8px 10px 6px" }}>{head}</div>
      {/* A plain string in `items` is a SECTION HEADER — Outreach's Add menu
          grouped by stage (2026-10-01). Every other caller passes rows only. */}
      {items.map((item, i) => (typeof item === "string" ? (
        <div key={i} style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: B.faint, fontWeight: 700, padding: "10px 12px 2px" }}>{item}</div>
      ) : (([ico, name, desc, disabled, onClick]) => (
        <div key={i} onClick={disabled ? undefined : onClick} style={{ display: "flex", gap: 11, padding: "11px 12px", borderRadius: 9, cursor: disabled ? "not-allowed" : "pointer", alignItems: "flex-start", opacity: disabled ? 0.55 : 1 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: disabled ? "var(--bg-quiet)" : B.tealQuiet, border: `1px solid ${disabled ? B.border2 : B.tealLine}`, color: disabled ? B.faint : B.teal, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>{ico}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: disabled ? B.dim : B.text }}>{name}</span>
              {disabled && <span style={{ ...pillBase, color: B.faint, border: `1px solid ${B.ghost}` }}>Soon</span>}
            </div>
            <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.5, marginTop: 3 }}>{desc}</div>
          </div>
        </div>
      ))(item)))}
    </div>
  );
}


/* A flat row-action icon. Hover has to be JS here — every element in this app is
   styled inline, and an inline style can't carry a :hover. */
/**
 * "Write this with Brill", as the bubble the row already uses.
 *
 * Her read, 2026-09-17: "all these 'write this section with Brill' could
 * probably just be a chat bubble right?" They could. Every row in every panel
 * already carries a MessageSquare that means EXACTLY this — open the chat on
 * this thing — and beside it these were the same instruction spelled out in
 * four words, sitting in a header that also holds the state, the word count and
 * the saved tick. Five of them, one per panel.
 *
 * THE WORDS ARE NOT LOST, they move into the tooltip and the aria-label: the
 * label is still what it always was ("Write this module with Brill"), so it is
 * read aloud and shown on hover, and the screen stops saying it twice.
 *
 * It is DELIBERATELY the same icon at both scopes. A different icon for "chat
 * about the whole record" would be a second thing to learn for the same act;
 * what changes is where it sits — the record's header, or the row.
 */
export function ChatWithBrill({ label, onClick, size = 15 }) {
  return <IconBtn icon={MessageSquare} label={label} onClick={onClick} size={size} />;
}

/* Something missing BEHIND a cog, said beside it. Her ask, 2026-09-24: a site
   made before its offer has no offer, and "if it's not linked the setting icon
   should have a warning next to it and say link your offer" — because the
   missing link is invisible from outside the cog and Brill writes blind without
   it. Amber (rule 5: attention). Pressing it opens the cog, since that is where
   the fix is. Knows nothing about offers: the caller says what is missing. */
export function CogNudge({ label, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 4, padding: "4px 10px", borderRadius: 14,
        border: `1px solid ${B.amberLine}`, background: B.amberQuiet, color: B.amber,
        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 3, background: B.amberFill }} />
      {label}
    </button>
  );
}

export function IconBtn({ icon: Icon, label, onClick, size = 13, hover = "var(--accent)" }) {
  const [on, setOn] = useState(false);
  return (
    <button
      onClick={onClick} title={label} aria-label={label}
      onMouseEnter={() => setOn(true)} onMouseLeave={() => setOn(false)}
      style={{
        width: 24, height: 24, padding: 0, border: "none", background: "none",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", color: on ? hover : B.faint, flexShrink: 0,
      }}
    >
      <Icon size={size} />
    </button>
  );
}


/* Not a button — there is nothing to press. It reports what already happened,
   which is the only honest thing left to put here once everything on the screen
   saves itself. Amber when something is stopping the save, because "Saved" and
   silence look identical and she would find out later. */
export function SavedTick({ saving, problem }) {
  if (problem) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: B.amber }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: B.amberFill }} />{problem}
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: saving ? B.faint : B.teal }}>
      {saving ? "Saving…" : <><Check size={13} /> Saved</>}
    </span>
  );
}


/* The page's name IS the heading of this screen, so it should read as one. The
   input chrome only turns up when she's about to use it: hover on a desktop
   pointer, or focus — which is what a tap gives, since touch has no hover.
   The box keeps its size in both states (a transparent border rather than no
   border), so nothing shifts when the lines arrive.

   An empty name keeps its lines. On a page she just added there is nothing to
   read yet, and a heading-shaped hole is not obviously the thing to type in. */
/* `compact` is the phone's heading (Social, her ask 2026-10-06): smaller, one
   line, cut off with an ellipsis, and sharing its row with the cog and bin. */
export function PageTitleField({ value, onChange, onCommit, placeholder = "Name this page", label = "Page name", compact = false }) {
  const [hot, setHot] = useState(false);
  const [on, setOn] = useState(false);
  const bare = !hot && !on && !!String(value || "").trim();
  return (
    <input value={value} onChange={(e) => onChange(e.target.value)}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      onFocus={() => setOn(true)} onBlur={() => { setOn(false); onCommit?.(); }}
      placeholder={placeholder} aria-label={label}
      style={{
        ...input, flex: compact ? "1 1 0" : "1 1 260px", minWidth: 0, fontWeight: 700,
        fontSize: compact ? 16 : 19, padding: compact ? "7px 8px" : "11px 14px",
        textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden",
        border: `1px solid ${bare ? "transparent" : B.border2}`,
        background: bare ? "transparent" : "var(--input-bg)",
        cursor: bare ? "pointer" : "text",
        transition: "background 120ms ease, border-color 120ms ease",
      }} />
  );
}


/* The breadcrumb, and — since 2026-09-15 — the only place a panel explains
   itself.
 *
 * TWO THINGS DROVE THIS, both hers.
 *
 * `crumb` names the PANEL when the heading no longer can. A record screen's
 * heading is the record ("The Business Brilliance Accelerator"), so the word
 * Courses had nowhere left to be said: "it should say course next to build, so
 * it shows what you are on." The landing screen passes no crumb, because its
 * own heading already says Courses and a breadcrumb that repeats the title
 * underneath it is not a trail, it is an echo.
 *
 * `hint` is where the prose went. Courses carried two paragraphs — a
 * three-line description under the heading and a four-line Note under the
 * table — roughly eighty words explaining a screen she has used for weeks:
 * "that is a lot of words, I'm not sure we need any of it, but can we
 * consolidate whatever is most important and maybe just put it by a tooltip."
 * That is rule 9 ("no caption explaining behaviour she has already watched
 * work") arriving where it had not been applied. What survives is what she
 * cannot deduce by looking: the boundary of what the panel holds, and what a
 * destructive-sounding button actually does.
 *
 * CLICK, NOT HOVER. Hover has no touch equivalent, and this is the only copy on
 * the screen that says what Pull from My Offer will not overwrite. */
export function Bread({ text, crumb, hint }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6, position: "relative" }}>
      <span style={{ fontSize: 11, color: B.faint, letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 700 }}>
        {text}{crumb ? ` · ${crumb}` : ""}
      </span>
      {hint && (
        <>
          <button onClick={() => setOpen((v) => !v)} aria-label={open ? "Hide what this is for" : "What is this for?"}
            aria-expanded={open}
            style={{ width: 15, height: 15, padding: 0, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: "none", color: open ? B.teal : B.faint, border: `1px solid ${open ? B.tealLine : B.border2}` }}>
            <Info size={9} />
          </button>
          {open && (
            <>
              <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
              <div style={{ position: "absolute", top: "calc(100% + 7px)", left: 0, zIndex: 20, width: 430, maxWidth: "88vw", background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 10, padding: "12px 14px", boxShadow: "var(--shadow-menu)", fontSize: 12.5, color: B.mid, lineHeight: 1.6, letterSpacing: 0, textTransform: "none", fontWeight: 400 }}>
                {hint}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}


export function Back({ onClick, label }) { return <div onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: B.teal, cursor: "pointer", marginBottom: 14 }}><ArrowLeft size={13} /> {label}</div>; }


// Capped like `pDesc`: a wider panel should give the TABLES more room, not
// stretch a line of italic prose across the whole screen.
export function Note({ children }) { return <div style={{ fontSize: 12, color: B.mid, fontStyle: "italic", marginTop: 18, lineHeight: 1.6, maxWidth: 720 }}>{children}</div>; }


export function SubPage({ title, sub }) { return <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, padding: "13px 16px", background: B.surface }}><div style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</div>{sub && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 2 }}>{sub}</div>}</div>; }


/* `action` puts a control on the label row — where the per-field Brill assists sit.
   Optional, so every existing caller is unaffected. */
export function Field({ label, children, action }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, minHeight: action ? 30 : 0 }}>
        <div style={{ flex: 1, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>{label}</div>
        {action}
      </div>
      {children}
    </div>
  );
}


/* The chat half of it. Extracted when the Website panel became its second
   consumer: the Offer Ladder's brainstorm and the section drafter are the same
   pane down to the bubble colours, and two inline copies would drift the first
   time either was touched. Everything tool-specific — what it's called, what it
   can see, what it suggests, what sits under it — comes in as a prop.

   Not its own file. It reads MomentumHub's `B` palette and sits with the other
   three pieces of this pattern; a shared file would move one of four. */
export function ChatPane({
  title, msgs = [], sending, chatText, setChatText, onSend, contextItems = [],
  suggestions = [], placeholder = "Think out loud with Brill…", height = CHAT_HEIGHT, footer, pending,
  tone = "chrome",
  /* A slot at the right of the header, for a control that belongs to the pane
     rather than to the conversation — the drafting screen's split modes. */
  action = null,
  /* A slot beside Send — the drafting screen's Next button. */
  beside = null,
  /* NO HEADER. The drafting screen names what she is writing in its one row
     above, so the card's own title and "Reading your locked content" were a
     second answer to the same question — her call, 2026-09-24: "take out
     reading your locked content for one more line." The folded context row
     ("Reading 4 of 5") still says Brill is reading her foundation. */
  headless = false,
  /* Fold the context chips to a count at every width (ContextChips `fold`). */
  foldContext = false,
}) {
  /* Two headers, because the two screens this sits on are different shapes.
     A full-width brainstorm gets the dark chrome bar every panel header uses.
     Beside a form, at half the width, that bar reads as a second app chrome
     inside the page — so the drafting screen gets a quiet one on the card, with
     the lock line under the title instead of across from it. Her mockups show
     exactly this difference. */
  const chrome = tone === "chrome";
  /* THE ACCELERATOR'S TYPE (ChatPanel.jsx): 13px, 14 on a phone, at 1.7 line
     height. The bubbles were 12.5 at 1.5, and her read of the two side by side
     was "accelerator you can read" (2026-09-24). */
  const narrowChat = !useTwoPane();
  return (
    <div style={{ border: `1px solid ${B.border}`, borderRadius: 14, background: B.surface, display: "flex", flexDirection: "column", height, overflow: "hidden" }}>
      {/* Chrome tokens are always dark, so the chrome header reads identically in
          both panel modes and must never take a canvas token. */}
      {!headless && <div style={{
        padding: chrome ? "14px 18px" : "13px 16px",
        background: chrome ? "var(--chrome-bg)" : B.surface,
        borderBottom: chrome ? "none" : `1px solid ${B.border}`,
        display: "flex", alignItems: chrome ? "center" : "flex-start",
        justifyContent: "space-between", gap: 12, flexWrap: "wrap",
      }}>
        <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 9, fontSize: 13.5, fontWeight: 700, color: chrome ? "var(--chrome-text)" : B.text }}>
          <span style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: chrome ? "var(--chrome-accent)" : B.teal, color: chrome ? "var(--chrome-on-accent)" : "var(--btn-text)", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>B</span>
          <span>
            {title}
            {!chrome && (
              <span style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: B.teal }}>
                <Lock size={10} /> Reading your locked content
              </span>
            )}
          </span>
        </span>
        {chrome && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--chrome-accent)" }}>
            <Lock size={11} /> Reading your locked content
          </span>
        )}
        {action}
      </div>}
      {contextItems.length > 0 && <ContextChips items={contextItems} fold={foldContext} />}
      {/* Bubbles sit on the canvas base, not the card, so the Brill bubble can use
          --surface-grey the way the real ChatPanel does. On a light canvas both
          --bg-surface and --surface-grey are white. */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", background: B.bg }}>
        {msgs.map((m, i) => {
          const mine = m.role === "user";
          return (
            <div key={i} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
              <div style={{ maxWidth: "88%", fontSize: narrowChat ? 14 : 13, lineHeight: 1.7, padding: narrowChat ? "10px 12px" : "11px 14px", borderRadius: 12, whiteSpace: "pre-wrap",
                background: mine ? B.tealQuiet : "var(--surface-grey)",
                border: `1px solid ${mine ? B.tealLine : B.border}`,
                color: mine ? B.teal : B.text }}>{m.content}</div>
            </div>
          );
        })}
        {sending && <div style={{ fontSize: 12, color: B.faint, padding: "2px 2px 6px" }}>Brill is thinking…</div>}
        {/* Under the reply it belongs to, inside the scroll area — not pinned
            below the input, where it would sit apart from the words it is
            offering. Hidden while a new reply is coming, since that reply is
            about to replace it. */}
        {!sending && pending}
        {/* Suggestions stay available after every reply, not just the opener — the
            useful ones are the ones you think of mid-conversation. */}
        {!sending && suggestions.length > 0 && msgs[msgs.length - 1]?.role === "assistant" && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
            {suggestions.map((q) => (
              <button key={q} style={quickBtn} onClick={() => onSend(q)}>{q}</button>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, padding: "12px 14px", borderTop: `1px solid ${B.border}` }}>
        <input value={chatText} onChange={(e) => setChatText(e.target.value)} disabled={sending}
          onKeyDown={(e) => { if (e.key === "Enter") onSend(chatText); }}
          placeholder={sending ? "Waiting for Brill…" : placeholder} style={{ ...input, flex: 1 }} />
        <button style={{ ...btnPrimary, padding: "9px 15px", opacity: sending ? 0.6 : 1, cursor: sending ? "default" : "pointer" }} disabled={sending} onClick={() => onSend(chatText)}>Send</button>
        {beside}
      </div>
      {footer}
    </div>
  );
}


export function ModeToggle({ mode, setMode }) {
  const seg = (on) => ({
    padding: "8px 18px", borderRadius: 20, fontSize: 13, fontWeight: on ? 700 : 600,
    cursor: "pointer", fontFamily: "inherit", border: "none",
    background: on ? B.surface : "transparent", color: on ? B.text : B.dim,
    boxShadow: on ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
  });
  return (
    <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 22, background: B.surface2, border: `1px solid ${B.border}`, flexShrink: 0 }}>
      <button style={seg(mode === "form")} onClick={() => setMode("form")}>Form</button>
      <button style={seg(mode === "brainstorm")} onClick={() => setMode("brainstorm")}>Brainstorm</button>
    </div>
  );
}


// The per-field ask. Deliberately never writes on its own — it opens Brill with a
// question, and she decides what comes back into the field.
export function AssistButton({ label, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} title={disabled ? "Type something first and I'll work from it" : undefined}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600,
        fontFamily: "inherit", cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0,
        border: `1px solid ${disabled ? B.border2 : B.tealLine}`, background: disabled ? "transparent" : B.tealQuiet, color: disabled ? B.faint : B.teal }}>
      <Wand2 size={13} /> {label}
    </button>
  );
}


/* What Brill can actually see, shown as fact rather than decoration. Each chip is
   driven by whether that content really exists — a chip claiming "My Offer
   (locked)" when she has locked nothing would be worse than showing no chips.
   Missing ones stay visible but dimmed, so the row doubles as "go lock this". */
/* WHAT BRILL IS READING, and on a phone that is a summary until she asks.
 *
 * Her report, 2026-09-18: "the context is getting too big on the chat. In
 * mobile it dominates. It could just show icons and then expand or something."
 * Five chips wrapped to three rows above a chat that had not started yet — the
 * thing she came to do was below the fold, under a list of things she already
 * knows are true.
 *
 * Collapsed it says how many of them Brill is actually reading, which is the
 * only part she needs at a glance and the part the chips make her count. It
 * opens to the full set, and a wide screen never collapses: there the row costs
 * nothing.
 *
 * A COUNT RATHER THAN ICONS, which is what she suggested. "Your voice",
 * "Positioning" and "Hero statement" have no icons anyone would recognise, and
 * five invented glyphs would be five things to learn in place of two words.
 */
export function ContextChips({ items = [], fold = false }) {
  /* `fold`: collapse at EVERY width, not just a phone. The drafting screen asks
     for it (2026-09-24): on a laptop the two rows of chips sat between a
     four-line conversation and the top of the screen, and "a wide screen
     never collapses, because there the row costs nothing" turned out not to be
     true once the screen was short rather than narrow. Other panes keep the
     old rule. */
  /* Both hooks above every return — a component that runs a different number of
     them depending on width is React #310 (src/hookOrder.test.js). */
  const narrow = useBrowseNarrow() || fold;
  const [open, setOpen] = useState(false);

  const reading = items.filter((i) => i.on).length;
  /* SHORTER AGAIN ON A PHONE (2026-09-22). Her read of the collapsed row: "we
     did reduce context so that's good but it needs more." It is a line she
     reads once and then works past, so on a phone it takes a line's worth of
     room and not a control's. */
  const bar = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: narrow ? "8px 14px" : "12px 16px", borderBottom: `1px solid ${B.border}`, background: B.surface };
  const head = { fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginRight: 2 };

  if (narrow && !open) {
    return (
      <button onClick={() => setOpen(true)} aria-expanded="false"
        title={`Brill is reading ${reading} of ${items.length} — tap to see which`}
        style={{ ...bar, width: "100%", boxSizing: "border-box", minHeight: 36, cursor: "pointer",
          border: "none", borderBottom: `1px solid ${B.border}`, fontFamily: "inherit", textAlign: "left" }}>
        <span style={head}>Context</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: reading ? B.teal : B.faint }}>
          Reading {reading} of {items.length}
        </span>
        <ChevronDown size={14} color={B.faint} style={{ marginLeft: "auto" }} />
      </button>
    );
  }

  return (
    <div style={bar}>
      <span style={head}>Context</span>
      {items.map(({ label, on }) => (
        <span key={label} title={on ? "Brill is reading this" : "Nothing locked here yet"}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, borderRadius: 18, padding: "5px 12px",
            border: `1px solid ${on ? B.tealLine : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.faint }}>
          {on ? <Check size={12} /> : null}{label}
        </span>
      ))}
      {narrow && (
        <button onClick={() => setOpen(false)} aria-expanded="true" aria-label="Hide the context"
          style={{ marginLeft: "auto", background: "none", border: "none", padding: 8, margin: -8, cursor: "pointer", color: B.faint, display: "flex" }}>
          <ChevronUp size={14} />
        </button>
      )}
    </div>
  );
}


export function Hint({ children }) { return <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>{children}</div>; }


export function Badge({ cls, text }) {
  const map = {
    trust: [B.teal, B.tealQuiet, B.tealLine],
    core: [B.salmon, B.salmonQuiet, B.salmonLine],
    cont: [B.amber, B.amberQuiet, B.amberLine],
    /* NEUTRAL: the pill SHAPE without spending a colour on it. For a category
       that is not a state — a site's kind — sitting in a row whose next column
       is the state dots.

       Her call, 2026-09-15, having asked whether kind could be coloured like
       the Offer Ladder's Type: "neutral is good for now, and then use the dots
       — I think the pill will help it feel better." Which is the right read.
       Teal a thumb from a teal dot would mean "this is a main site" beside
       "this page is finished"; amber and salmon are both spoken for (attention,
       danger); and the app's indigo is a CHROME token that reads as a hole on
       the dark canvas. The structure was what the row was missing, not a hue.

       Colour also earns most where it marks the exception, and nearly every
       site is a main site.

       FILLED, not an outline. The first version was faint text on transparent
       inside a faint border, and her read of it was immediate: "it's bland right
       now, your eye doesn't want to read it." Neutral is about not spending a
       HUE; it is not about being hard to see. A sunken fill and readable text
       give the pill the presence the other three have. */
    neutral: [B.mid, B.surface2, B.border2],
  };
  const [c, bg, bd] = map[cls] || map.trust;
  return <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: c, background: bg, border: `1px solid ${bd}` }}>{text}</span>;
}


/* `text` is optional — offers derive their label from the status value, while the
   website and CTA tables pass their own wording ("Empty", "Locked"). Retired is
   deliberately neutral rather than a warning: it's an intended end state, not a
   problem to fix. */
/* ONE DOT PER RECORD, and the count beside them.
 *
 * Her call, 2026-09-15, looking at the new landing tables: "should it have the
 * number of draft versus green dots on the sites table — it is less colourful, I
 * think we took them off but now we could have them and they'd make more sense…
 * with 5 dots if there are 5 pages, or 1 dot if there is 1." Which is right:
 * they were taken off with the pages table, and they mean MORE now than they did
 * then, because Done is a real mark she sets rather than "has words".
 *
 * I argued for a count ("2 of 5 done") on the grounds that a site with thirty
 * pages is thirty dots. She wants the dots, and she is the one reading the
 * screen every day — the row wraps, and real sites are five to ten pages.
 *
 * WHY THIS IS SAFE HERE when a derived L2 state was not. What we deleted the
 * same morning was a PAGE's colour computed from its sections, competing with
 * marks those sections also carried — two answers to one question. A site has no
 * mark of its own and never will, so this is not a second opinion about
 * anything. It is arithmetic on the very field she set, and it cannot disagree
 * with the rows it counts.
 *
 * Grew out of `SectionDots`, which had been orphaned in website.jsx since the
 * pages table became the sites table. Same shape, no longer knowing what it is
 * counting: it is handed states and a noun.
 */
export function StateDots({ states = [], noun = "page", titles = [] }) {
  if (!states.length) return <span style={{ fontSize: 12, color: B.ghost }}>No {noun}s</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", minWidth: 0 }}>
      {states.map((state, i) => (
        <span key={i} title={titles[i] || state}
          style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: B[STATUS_DOT[state]] || B.ghost }} />
      ))}
      <span style={{ fontSize: 11.5, color: B.faint, marginLeft: 3, whiteSpace: "nowrap" }}>
        {states.length} {noun}{states.length === 1 ? "" : "s"}
      </span>
    </div>
  );
}


export function Status({ cls, text, dotOnly = false }) {
  const label = text || STATUS_LABELS[cls] || "Draft";
  const retired = cls === "retired";
  const locked = cls === "locked";
  // The dot is a fill and the label is text, so they take different tokens on
  // the amber side — `--warn-text` as a 7px dot would read as brown.
  const c = retired ? B.faint : locked ? B.teal : B.amber;
  const dot = retired ? B.ghost : locked ? B.teal : B.amberFill;
  /* THE DOT ALONE, for a scanning list on a phone where the word costs a line
     it cannot spare. Same function rather than a second dot somewhere else:
     two places deciding what colour "retired" is, is how one list ends up
     disagreeing with another. The label survives as the accessible name. */
  if (dotOnly) {
    return <span title={label} aria-label={label} role="img"
      style={{ width: 9, height: 9, borderRadius: "50%", background: dot, display: "inline-block", flexShrink: 0 }} />;
  }
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: c }}><span style={{ width: 7, height: 7, borderRadius: "50%", background: dot }} />{label}</span>;
}


// Provenance: this record came across from the Accelerator rather than being
// typed in here. Quiet by design — it's a fact about the record, not a status.
// `label` names the module it came from; the default is the offer ladder, which
// was the only caller when this was written.
export function SourceTag({ label = "From My Offer" }) {
  return <span style={{ ...pillBase, color: B.label, border: `1px solid ${B.border2}`, background: B.surface2, padding: "2px 7px", fontWeight: 600, letterSpacing: "0.04em", textTransform: "none", fontSize: 10 }}>{label}</span>;
}


export function RefreshTag() {
  return <span style={{ ...pillBase, color: B.amber, border: `1px solid ${B.amberLine}`, background: B.amberQuiet, padding: "2px 7px" }}>May need refresh</span>;
}


// The pill toggle used by pricing mode, payment plan and status.
export function Choice({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: "7px 15px", borderRadius: 20, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
      border: `1px solid ${on ? B.teal : B.border2}`,
      background: on ? B.tealQuiet : B.surface,
      color: on ? B.teal : B.mid,
    }}>{children}</button>
  );
}


export function SoonBadge() { return <span style={soonBadge}>Coming soon</span>; }


/* ── The two coming-soon panels ───────────────────────────────────────────────
   HOW THESE GOT LOST. They were written beside Funnels in the 3,700-line file,
   moved into `momentum/funnels.jsx` with it in the split (e8c24f0) purely
   because of where they sat on screen, and MomentumHub went on calling them
   WITHOUT importing them -- so Admin, Onboarding and Client Success have thrown
   `RunOverview is not defined` since that day. Deleting funnels.jsx only made a
   standing crash visible.

   They live here now because that is what they are: panel-agnostic chrome that
   knows nothing about a page, a module, an offer or a funnel. Same reason as
   everything else in this file. They move out again when either gets built.

   The no-undef guard does NOT catch this -- a bare `<RunOverview />` in JSX is
   not flagged without the React plugin -- which is why it survived a clean
   build and a clean lint twice. See the regression test beside this file.       */
export function RunOverview({ title, desc, rows, note }) {
  return (
    <>
      <Bread text="Run" />
      <h2 style={pTitle}>{title} <span style={{ ...soonBadge, verticalAlign: "middle", marginLeft: 8 }}>Coming soon</span></h2>
      <p style={pDesc}>{desc}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map(([t, sub], i) => <SubPage key={i} title={t} sub={sub} />)}
      </div>
      <Note>{note}</Note>
    </>
  );
}


export function SocialNote() {
  return (
    <>
      <Bread text="Grow" />
      <h2 style={pTitle}>Social Generator</h2>
      <p style={pDesc}>Already live. Posts in your voice from your locked foundation — next up, content arcs (week / month) with a meeting link rotated in from Run.</p>
      <div style={soonCard}>
        <div style={{ fontSize: 26 }}>📣</div>
        <div><h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Shipped — the social arc is next</h3>
          <p style={{ margin: 0, color: B.mid, fontSize: 13, lineHeight: 1.6 }}>The one Grow content tool that already exists. After the Offer Ladder ADD, this is where we go.</p>
          <span style={{ ...soonBadge, color: B.teal, borderColor: B.tealLine, background: B.tealQuiet }}>Live · arc in progress</span>
        </div>
      </div>
    </>
  );
}
